import { FC, useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Button,
  DataRow,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Messages,
  Table,
  Tag,
  useAlert,
  useSimpleTableDataSource,
} from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading, Paragraph } from "capitalrx-components-2.0/lib/typography";
import Decimal from "decimal.js";

import {
  useGetMedicarePrescriptionPaymentPlanRefundStatus,
  useListMedicarePrescriptionPaymentPlanPaymentQuery,
} from "@/App/api/medicarePrescriptionPaymentPlan";
import { UniformRowColorTable } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context";
import { usePlanYear } from "@/App/pages";
import { RefundModal } from "@/App/pages/MedicarePrescriptionPaymentPlan/Participation/PaymentHistory/RefundModal";
import { MedicarePrescriptionPaymentPlanTransaction } from "@/App/types/medicarePrescriptionPaymentPlan";
import { formatDollars } from "@/App/utils/utils";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentHistoryPanel";

export const PaymentHistoryPanel: FC = () => {
  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const alert = useAlert();
  const { isViewingDataholder } = useContext(MemberEligibilityContext);
  const isEmulating = sessionStorage.getItem("isEmulation") === "true";

  const {
    data: payments,
    isLoading: isLoadingPayments,
    isError: isPaymentsError,
  } = useListMedicarePrescriptionPaymentPlanPaymentQuery({ page_number: 1, plan_year: planYear });

  const paymentHistoryRows: DataRow<MedicarePrescriptionPaymentPlanTransaction>[] = useMemo(() => {
    return (payments?.results || [])
      .filter((payment) => payment.tx_type === "payment" || payment.tx_type === "refund")
      .sort((a, b) => new Date(b.tx_date).getTime() - new Date(a.tx_date).getTime() || b.id - a.id)
      .map((payment) => {
        return {
          id: payment.id,
          data: {
            ...payment,
          },
        };
      });
  }, [payments]);

  const paymentHistoryDataSource = useSimpleTableDataSource(paymentHistoryRows);

  const {
    data: refundStatus,
    isLoading: isLoadingRefundStatus,
    isError: isErrorRefundStatus,
  } = useGetMedicarePrescriptionPaymentPlanRefundStatus(planYear);

  useEffect(() => {
    if (isErrorRefundStatus) {
      alert({
        message: translate(`${TRANSLATION_PATH}.unableToLoadRefundInformation`),
        type: "error",
        style: "banner",
      });
    }
  }, [isErrorRefundStatus]);

  const refundAmount = useMemo(() => {
    return new Decimal(refundStatus?.refundable_amount || 0);
  }, [refundStatus]);

  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);

  const closeRefundModal = () => {
    setIsRefundModalVisible(false);
  };

  const openRefundModal = () => {
    setIsRefundModalVisible(true);
  };

  return (
    <Stack>
      <Heading>
        <Shelf>
          {translate(`${TRANSLATION_PATH}.paymentHistory`)}
          <Tag type="info" style={{ display: "inline-flex", alignItems: "center" }}>
            {planYear}
          </Tag>
          <Shelf.Spacer />
        </Shelf>
      </Heading>

      <Message
        appearance={Messages.Informational}
        visible={!isErrorRefundStatus && !isLoadingRefundStatus && !!refundAmount.toDecimalPlaces(2).gt(0)}
      >
        <Shelf>
          <Paragraph>
            <Trans
              i18nKey={`${TRANSLATION_PATH}.eligibleForRefund`}
              values={{ refundableAmount: formatDollars(refundAmount.toString()) }}
              components={{ b: <b /> }}
            />
          </Paragraph>
          <Shelf.Spacer />
          <Button appearance="text" onClick={openRefundModal} disabled={isViewingDataholder || isEmulating}>
            {translate(`${TRANSLATION_PATH}.requestRefund`)}
          </Button>
        </Shelf>
      </Message>

      {isRefundModalVisible && <RefundModal maxRefund={refundAmount} isVisible={isRefundModalVisible} onClose={closeRefundModal} planYear={planYear} />}

      <LoadingGroup loading={isLoadingPayments}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          }
        >
          {!isPaymentsError && paymentHistoryRows.length > 0 && (
            <Shelf>
              <UniformRowColorTable dataSource={paymentHistoryDataSource}>
                <Table.Date
                  name="tx_date"
                  initialWidth="span"
                  sortable
                  title={translate(`${TRANSLATION_PATH}.dueDate`)}
                  format="LL/dd/yyyy"
                />
                <Table.Custom name="tx_amount" initialWidth="span" sortable title={translate(`${TRANSLATION_PATH}.amountPaid`)}>
                  {(value) => formatDollars(value as number)}
                </Table.Custom>
                <Table.Custom name="type" initialWidth="span" title={translate(`${TRANSLATION_PATH}.paymentMethod`)}>
                  {(method) => {
                    if (method === "electronic") {
                      return translate(`${TRANSLATION_PATH}.electronic`);
                    } else if (method === "check") {
                      return translate(`${TRANSLATION_PATH}.check`);
                    }
                  }}
                </Table.Custom>
                <Table.Custom name="tx_type" initialWidth="span" title={translate(`${TRANSLATION_PATH}.status`)} align="center">
                  {(_value, _id, row: DataRow<MedicarePrescriptionPaymentPlanTransaction>) => {
                    if (row.data.tx_type === "payment") {
                      if (row.data.status === "processing") {
                        return <Tag type="info-high-contrast">{translate(`${TRANSLATION_PATH}.paymentProcessing`)}</Tag>;
                      } else if (row.data.status === "failed") {
                        return <Tag type="error-high-contrast">{translate(`${TRANSLATION_PATH}.paymentFailed`)}</Tag>;
                      } else if (row.data.status === "canceled") {
                        return <Tag type="error-high-contrast">{translate(`${TRANSLATION_PATH}.paymentCanceled`)}</Tag>;
                      }
                      return <Tag type="success-high-contrast">{translate(`${TRANSLATION_PATH}.paid`)}</Tag>;
                    } else if (row.data.tx_type === "refund") {
                      if (row.data.status === "processing") {
                        return <Tag type="info-high-contrast">{translate(`${TRANSLATION_PATH}.refundProcessing`)}</Tag>;
                      } else if (row.data.status === "failed") {
                        return <Tag type="error-high-contrast">{translate(`${TRANSLATION_PATH}.refundFailed`)}</Tag>;
                      } else if (row.data.status === "canceled") {
                        return <Tag type="error-high-contrast">{translate(`${TRANSLATION_PATH}.refundCanceled`)}</Tag>;
                      }
                      return <Tag type="info-high-contrast">{translate(`${TRANSLATION_PATH}.refunded`)}</Tag>;
                    }
                  }}
                </Table.Custom>
              </UniformRowColorTable>
              <Shelf.Spacer />
            </Shelf>
          )}

          <Message appearance={Messages.Informational} visible={!paymentHistoryRows.length}>
            {translate(`${TRANSLATION_PATH}.noPaymentsYet`)}
          </Message>

          <Message visible={isPaymentsError} appearance={Messages.Error}>
            {translate(`${TRANSLATION_PATH}.noPaymentsError`)}
          </Message>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
